// Pointer/keyboard → move intent. Pure: no DOM, no canvas. Supports
// click-click and drag-and-drop interchangeably, the way chess sites do: a
// press on your own piece selects it and arms a drag; releasing on a legal
// square plays the move, releasing where you pressed leaves it selected for a
// second click. A pawn reaching the last rank asks for a promotion choice.
import { isColor, type Color, type Move, type Square } from '../game/types.js';
import type { Point } from '../render/geometry.js';
import type { LegalTarget } from '../render/overlay/InteractionOverlay.js';

/** Pointer travel (canvas px) before a press becomes a drag. */
export const DRAG_THRESHOLD_PX = 6;

export type SelectorResult =
  | { readonly kind: 'none' }
  | { readonly kind: 'move'; readonly move: Move }
  /** Several legal moves share from/to — the four promotions. Ask, then call `choosePromotion`. */
  | { readonly kind: 'promote'; readonly options: readonly Move[] };

const NONE: SelectorResult = { kind: 'none' };

export interface SelectorView {
  readonly selected: Square | null;
  readonly legalTargets: readonly LegalTarget[];
  readonly drag: { readonly from: Square; readonly at: Point } | null;
}

export class MoveSelector {
  private moves: readonly Move[] = [];
  private turn: Color = 'w';
  private board: Int8Array = new Int8Array(64);
  private selected: Square | null = null;
  private press: { from: Square; origin: Point } | null = null;
  private dragAt: Point | null = null;
  /** The press that selected the piece also started this gesture — a release there keeps it selected. */
  private freshSelection = false;

  /** Feed the current position. `moves` empty means it is not the local player's turn. */
  setPosition(board: Int8Array, turn: Color, moves: readonly Move[]): void {
    this.board = board;
    this.turn = turn;
    this.moves = moves;
    this.clear();
  }

  clear(): void {
    this.selected = null;
    this.press = null;
    this.dragAt = null;
  }

  view(): SelectorView {
    const targets: LegalTarget[] = [];
    if (this.selected !== null) {
      const seen = new Set<Square>();
      for (const m of this.movesFrom(this.selected)) {
        if (seen.has(m.to)) continue;
        seen.add(m.to);
        targets.push({ square: m.to, capture: m.captured !== 0 });
      }
    }
    const drag = this.press !== null && this.dragAt !== null ? { from: this.press.from, at: this.dragAt } : null;
    return { selected: this.selected, legalTargets: targets, drag };
  }

  pointerDown(square: Square | null, at: Point): SelectorResult {
    if (square === null) {
      this.clear();
      return NONE;
    }
    if (this.selected !== null && square !== this.selected) {
      const result = this.resolve(this.selected, square);
      if (result.kind !== 'none') {
        this.clear();
        return result;
      }
    }
    if (this.isOwnPiece(square)) {
      this.freshSelection = this.selected !== square;
      this.selected = square;
      this.press = { from: square, origin: at };
      return NONE;
    }
    this.clear();
    return NONE;
  }

  pointerMove(at: Point): void {
    if (this.press === null) return;
    if (this.dragAt === null && Math.hypot(at.x - this.press.origin.x, at.y - this.press.origin.y) < DRAG_THRESHOLD_PX) return;
    this.dragAt = at;
  }

  pointerUp(square: Square | null): SelectorResult {
    const press = this.press;
    const dragged = this.dragAt !== null;
    this.press = null;
    this.dragAt = null;
    if (press === null) return NONE;
    if (square !== null && square !== press.from) {
      const result = this.resolve(press.from, square);
      if (result.kind !== 'none') this.clear();
      return result;
    }
    // A plain click on an already-selected piece toggles it off; a drag back home keeps it.
    if (square === press.from && !dragged && !this.freshSelection) this.selected = null;
    return NONE;
  }

  /** Pointer left the board or the gesture was cancelled: drop the drag, keep the selection. */
  cancelGesture(): void {
    this.press = null;
    this.dragAt = null;
  }

  /** Keyboard: Enter on a square. Selects own pieces, moves to legal targets. */
  activate(square: Square): SelectorResult {
    if (this.selected !== null && square !== this.selected) {
      const result = this.resolve(this.selected, square);
      if (result.kind !== 'none') {
        this.clear();
        return result;
      }
    }
    if (this.isOwnPiece(square)) {
      this.selected = this.selected === square ? null : square;
      return NONE;
    }
    this.selected = null;
    return NONE;
  }

  getSelected(): Square | null {
    return this.selected;
  }

  private resolve(from: Square, to: Square): SelectorResult {
    const options = this.movesFrom(from).filter((m) => m.to === to);
    if (options.length === 0) return NONE;
    const [only] = options;
    if (options.length === 1 && only !== undefined) return { kind: 'move', move: only };
    return { kind: 'promote', options };
  }

  private movesFrom(sq: Square): Move[] {
    return this.moves.filter((m) => m.from === sq);
  }

  private isOwnPiece(sq: Square): boolean {
    const code = this.board[sq] ?? 0;
    return code !== 0 && isColor(code, this.turn) && this.moves.length > 0;
  }
}

/** The option matching a chosen promotion piece type. */
export function choosePromotion(options: readonly Move[], type: number): Move | undefined {
  return options.find((m) => m.promotion === type);
}
